import React, { useState, useEffect } from "react";
import { useTheme } from "../theme";
import { Disclosure, DisclosureButton, DisclosurePanel } from "../Disclosure";
import { SortBy } from "./SortBy";
import FilterButtonIcon from "./filter-disclosure-button-icon.svg";
import queryString from "query-string";
import { navigate } from "gatsby";

export function createListFilters({ primaryFilters, secondaryFilters, location}) {
    const allFilters = primaryFilters.concat(secondaryFilters);
    return ({
        items,
        render,
        values: filterValues = {},
        exhibit = null,
        onChange,
    }) => {
        const { contentWidth } = useTheme();
        const [isFiltersTouched, setIsFiltersTouched] = useState(false);
        const [hasQueryFilter, setQueryFilter] = useState(false);
        const [filters, setFilters] = useState(() =>
            Object.fromEntries(
                allFilters.map(({ key, initialValue }) => {
                    if(initialValue) {
                        setQueryFilter(true);
                    }
                    const value = filterValues[key]?.value || initialValue || null;
                    return [key, { value, filterFn: null }];
                })
            )
        );
        const [sortBy, setSortBy] = useState({
            value: null,
            sortFn: null,
        });

        useEffect(() => {
            if (!Object.keys(filterValues).length) {
                return;
            }

            Object.values(filterValues).some(({ value }) => value) &&
                setIsFiltersTouched(true);
            setFilters((filtersObj) => ({
                ...filtersObj,
                ...filterValues,
            }));
        }, [filterValues]);

        const updateQueryParms = (param) => {
            const query = queryString.parse(location.search);
            const searchParams = queryString.stringify({ ...query, ...{ series: param.key } });
            navigate(`?${searchParams}`);
        }
    
        const onFilterChange = (key, searchFields, value, filterFn) => {
            setIsFiltersTouched(true);
            setQueryFilter(false);

            // for now update only for series filter
            if(key==='series') {
                updateQueryParms(value);
            }

            const filterVal = {
                value,
                filterFn:
                    filterFn &&
                    ((item) =>
                        searchFields.some((field) => filterFn(field(item)))),
            };

            if (filterValues[key]) {
                return onChange({
                    [key]: filterVal,
                });
            }

            return setFilters({
                ...filters,
                [key]: filterVal,
            });
        };

        const onSortByChange = (value, sortFn) => {
            setIsFiltersTouched(true);
            setSortBy({
                value,
                sortFn,
            });
        };

        const filterFnToApply = Object.values(filters)
            .map(({ filterFn }) => filterFn)
            .filter(Boolean);

        let filterKey='';
        const filteredItems = filterFnToApply.length
            ? items.filter((item) => {
                  return filterFnToApply.every((filterFn) => filterFn(item));
              })
            : hasQueryFilter && (filterKey = Object.values(filters).filter(item => item&&item.value&&item.value.key)) && filterKey && filterKey[0]?.value?.key.toLowerCase() !== 'all'
            ? items.filter(({series})=> series?.slug === filterKey[0]?.value?.key)
            : items;

        const sortedFilteredItems =
            sortBy.sortFn?.(filteredItems) || filteredItems;

        return (
            <>
                <Disclosure open={isFiltersTouched || hasQueryFilter || null} id="list-filters">
                    <div className="list-filters__exhibit">{exhibit}</div>
                    <div className="list-filters__filters">
                        <div className="list-filters__container">
                            <div className="filters__wrapper">
                                <div className="filter-actions__exhibit">
                                    {exhibit}
                                </div>
                                {primaryFilters.map(
                                    ({
                                        key,
                                        searchFields,
                                        component: Filter,
                                    }) => (
                                        <Filter
                                            key={key}
                                            value={filters[key].value}
                                            className="list-filters__text-filter"
                                            onChange={(...args) =>
                                                onFilterChange(
                                                    key,
                                                    searchFields,
                                                    ...args
                                                )
                                            }
                                        />
                                    )
                                )}
                                <div className="filter-actions__secondary">
                                    <DisclosureButton
                                        icon={FilterButtonIcon}
                                        className="list-filters__filter-by-button"
                                    >
                                        Filter By
                                    </DisclosureButton>
                                    <SortBy
                                        value={sortBy.value}
                                        className="list-filters__sort-by-button"
                                        onChange={onSortByChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="list-filters__additional-filters">
                        <DisclosurePanel className="list-filters__panel">
                            {secondaryFilters.map(
                                ({ key, searchFields, component: Filter }) => (
                                    <Filter
                                        key={key}
                                        id={`${key}-filter`}
                                        value={filters[key].value}
                                        className="list-filters__select-filter"
                                        onChange={(...args) =>
                                            onFilterChange(
                                                key,
                                                searchFields,
                                                ...args
                                            )
                                        }
                                    />
                                )
                            )}
                        </DisclosurePanel>
                    </div>
                </Disclosure>
                {render(sortedFilteredItems, isFiltersTouched)}
                <style jsx>{`
                    .list-filters__filters {
                        background-color: rgba(255, 255, 255, 0.2);
                        padding: 0.5em 1.25em 1em;
                    }
                    .list-filters__container {
                        max-width: ${contentWidth}em;
                        margin: 0 auto;
                    }
                    .filters__wrapper {
                        margin-left: -1.25em;
                        display: flex;
                        flex-wrap: wrap;
                        flex-direction: row-reverse;
                        align-items: center;
                    }
                    .list-filters__exhibit {
                        margin: 0 0 1.5em;
                    }
                    .filter-actions__exhibit {
                        display: none;
                    }
                    @media (min-width: 65em) {
                        .filters__wrapper {
                            flex-wrap: nowrap;
                        }
                        .list-filters__exhibit {
                            display: none;
                        }
                        .filter-actions__exhibit {
                            display: block;
                            margin: -2em 0 -2em 1.25em;
                            flex: 99 99 15em;
                            order: 2;
                        }
                    }
                    .filter-actions__secondary {
                        margin-right: auto;
                        flex: 1 1 20em;
                        display: flex;
                        flex-wrap: wrap;
                        align-items: center;
                        order: 3;
                    }
                    @media (min-width: 30em) {
                        .filter-actions__secondary {
                            flex-wrap: nowrap;
                        }
                    }
                    .list-filters__filters
                        :global(.list-filters__filter-by-button) {
                        margin: 0.5em 0 0 1.25em;
                    }
                    .list-filters__filters
                        :global(.list-filters__sort-by-button) {
                        margin: 0.5em 0 0 1.25em;
                    }
                    .list-filters__filters :global(.list-filters__text-filter) {
                        margin: 0.5em 0 0 1.25em;
                        flex: 1 1 20em;
                        display: flex;
                        align-items: center;
                    }
                    @media (min-width: 44em) {
                        .list-filters__filters
                            :global(.list-filters__text-filter) {
                            max-width: 20em;
                        }
                    }
                    .list-filters__additional-filters {
                        padding: 0 1.25em;
                        max-width: ${contentWidth + 1.25 * 2}em;
                        margin: 0 auto;
                    }
                    .list-filters__additional-filters
                        :global(.list-filters__panel) {
                        margin-left: -1.5em;

                        display: flex;
                        flex-wrap: wrap;
                    }
                    .list-filters__additional-filters
                        :global(.list-filters__select-filter) {
                        margin: 1.5em 0 0 1.5em;
                        width: 30%;
                        min-width: 10em;
                        max-width: 35em;

                        flex: 1 1 10em;
                        // Holy albatross
                        flex-basis: calc((35em - 100%) * 999);
                    }
                `}</style>
            </>
        );
    };
}
